import { useParams } from 'react-router-dom'

import {
  CreateActionItemDocument,
  CreateActionItemMutationVariables,
  GetProcessDetailDocument,
  UpdateActionItemDocument,
} from 'generated/resources/graphql'

import { useMutation } from 'modules/core/hooks/useMutation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { ValidationError } from 'modules/core/lib/errors'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'

import { AssigneeStrategy } from 'modules/processes/components/ItemAssignees/types'

interface CreateActionItemProps {
  createVariables: CreateActionItemMutationVariables
  processId: number
}

interface UpdatetItemProps extends CreateActionItemProps {
  actionId: number
  itemId: number
  filesToRemove?: number[]
}

export const useUpsertActionItem = () => {
  const [executeMutation, { loading }] = useMutation(CreateActionItemDocument, {
    extractorFn: (data) => data.processesMutations.createProcessesAction,
  })

  const { category } = useParams()

  const [executeUpdate, { loading: loadingUpdate }] = useMutation(UpdateActionItemDocument, {
    extractorFn: (data) => data.processesMutations.patchProcessesAction,
  })

  const commonMutationProps = (processId: number) => ({
    awaitRefetchQueries: true,
    refetchQueries: [
      {
        query: GetProcessDetailDocument,
        variables: {
          id: processId,
        },
      },
    ],
  })

  const updateItem = async ({
    actionId,
    itemId,
    createVariables,
    filesToRemove,
    processId,
  }: UpdatetItemProps) => {
    const result = await executeUpdate({
      variables: {
        id: +actionId,
        ...createVariables,
        filesToRemove,
        item: {
          id: +itemId,
          position: createVariables.item.position,
          stepId: createVariables.item.stepId,
          assigneeStrategy: createVariables.item.assigneeStrategy || AssigneeStrategy.AFFECTED_USER,
          assigneeIds: createVariables.item.assigneeIds,
        },
      },
      ...commonMutationProps(processId),
    })

    return handleResult(result, () => undefined)
  }
  const createItem = async ({ createVariables, processId }: CreateActionItemProps) => {
    const result = await executeMutation({
      variables: createVariables,
      ...commonMutationProps(processId),
    })

    track(trackingEvents.processes.createActionSaved, {
      processId,
      category,
      configurationIdentifier: createVariables.configurationIdentifier,
    })

    return handleResult(result, () => undefined)
  }

  const upsertItem = async (
    createVariables: CreateActionItemMutationVariables,
    processId: number,
    actionId?: number,
    itemId?: number,
    filesToRemove?: number[]
  ) => {
    if (actionId && itemId) {
      return updateItem({ actionId, itemId, createVariables, filesToRemove, processId })
    }

    return createItem({ createVariables, processId })
  }

  const upsertActionItem = async (
    createVariables: CreateActionItemMutationVariables,
    processId: number,
    actionId?: number,
    itemId?: number,
    filesToRemove?: number[]
  ) => {
    const result = await upsertItem(createVariables, processId, actionId, itemId, filesToRemove)

    if (!result.success)
      throw new ValidationError({ errors: i18n.t('processes.process_settings.save_error') })

    return result.success ? result.data : undefined
  }

  return {
    upsertActionItem,
    loading: loading || loadingUpdate,
  }
}
